import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtL, fmtDate, apiErr } from "@/lib/format";
import { toast } from "sonner";
import {
  PlusIcon, XIcon, HandCoinsIcon, CalendarIcon, CheckCircleIcon, PaperPlaneTiltIcon,
  WarningCircleIcon, ChartLineIcon, BankIcon, CaretRightIcon,
} from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

export default function Loans() {
  const [tab, setTab] = useState("dashboard");
  const [loans, setLoans] = useState([]);
  const [summary, setSummary] = useState(null);
  const [banks, setBanks] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [openDetail, setOpenDetail] = useState(null);

  const load = async () => {
    const [l, s, b, ob] = await Promise.all([
      api.get("/accounting/loans"),
      api.get("/accounting/loans/dashboard/summary"),
      api.get("/treasury/accounts"),
      api.get("/accounting/loans/obligations").catch(() => ({ data: null })),
    ]);
    // Saldo total convertido a HNL (préstamos en USD × tasa de cambio)
    const sum = ob.data ? { ...s.data, total_vigente: ob.data.saldo_total_hnl, total_proximas_30_dias: ob.data.total_proximas_hnl } : s.data;
    setLoans(l.data); setSummary(sum); setBanks(b.data);
  };
  useEffect(() => { load(); }, []);

  const onSaved = () => { setOpenNew(false); load(); };

  return (
    <Layout
      title="Préstamos y Obligaciones"
      subtitle={`${loans.length} préstamos · Saldo vigente ${fmtL(summary?.total_vigente || 0)} · PRD 4.8`}
      actions={
        <button
          onClick={() => setOpenNew(true)}
          data-testid="new-loan-btn"
          className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2"
        >
          <PlusIcon size={16} weight="bold" /> Registrar préstamo
        </button>
      }
    >
      <div className="border-b border-border mb-5 flex gap-1 overflow-x-auto">
        {[
          { k: "dashboard", l: "Tablero", i: ChartLineIcon },
          { k: "list", l: "Listado", i: HandCoinsIcon },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`tab-loans-${t.k}`}
            className={`px-4 py-2.5 text-sm transition-all relative flex items-center gap-2 whitespace-nowrap ${
              tab === t.k ? "text-agro-green" : "text-text-secondary hover:text-text-primary"
            }`}
          >
            <t.i size={14} weight="duotone" /> {t.l}
            {tab === t.k && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-agro-green" />}
          </button>
        ))}
      </div>

      {tab === "dashboard" && summary && <DashboardView summary={summary} loans={loans} onOpen={setOpenDetail} />}
      {tab === "list" && <ListView loans={loans} onOpen={setOpenDetail} />}

      {openNew && <LoanForm banks={banks} onClose={() => setOpenNew(false)} onSaved={onSaved} />}
      {openDetail && (
        <LoanDetail
          loanId={openDetail}
          banks={banks}
          onClose={() => setOpenDetail(null)}
          onChange={() => load()}
        />
      )}
    </Layout>
  );
}

function KpiCard({ label, value, color = "green", icon: Icon }) {
  const colorClass = { green: "text-agro-green", yellow: "text-agro-yellow", red: "text-red-400", blue: "text-blue-400" }[color];
  return (
    <div className="bg-card border border-border/60 rounded-lg p-5">
      <div className="flex items-center gap-2 mb-2">
        {Icon && <Icon size={14} className="text-text-muted" />}
        <span className="text-[10px] uppercase tracking-[0.2em] text-text-muted">{label}</span>
      </div>
      <div className={`font-heading text-2xl font-semibold ${colorClass}`}>{value}</div>
    </div>
  );
}

function DashboardView({ summary, loans, onOpen }) {
  const proximas = summary.proximas_30_dias || [];
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        <KpiCard label="Saldo capital vigente" value={fmtL(summary.total_vigente)} color="green" icon={HandCoinsIcon} />
        <KpiCard label="Préstamos vigentes" value={summary.cantidad_prestamos_vigentes} color="yellow" icon={BankIcon} />
        <KpiCard label="Cuotas próximas (30 días)" value={fmtL(summary.total_proximas_30_dias)} color="red" icon={CalendarIcon} />
      </div>

      <div className="bg-card border border-border/60 rounded-lg overflow-hidden" data-testid="proximas-cuotas">
        <div className="p-4 border-b border-border/60 flex items-center justify-between">
          <h3 className="font-heading text-sm uppercase tracking-wider text-text-muted">Próximas cuotas</h3>
          <span className="text-xs text-text-muted">{proximas.length} pendientes</span>
        </div>
        <table className="w-full">
          <thead className="bg-sidebar">
            <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
              <th className="p-3 pl-5">Fecha</th>
              <th className="p-3">Préstamo</th>
              <th className="p-3">Acreedor</th>
              <th className="p-3">Cuota #</th>
              <th className="p-3 text-right pr-5">Monto</th>
              <th className="p-3 pr-5"></th>
            </tr>
          </thead>
          <tbody>
            {proximas.length === 0 ? (
              <tr><td colSpan="6" className="p-10 text-center text-text-muted text-sm">No hay cuotas próximas en los próximos 30 días</td></tr>
            ) : proximas.map((p, i) => (
              <tr key={i} className="border-t border-border/30 hover:bg-white/[0.02]">
                <td className="p-3 pl-5 text-xs">{fmtDate(p.fecha)}</td>
                <td className="p-3 font-mono text-xs text-agro-green">{p.codigo}</td>
                <td className="p-3 text-sm">{p.acreedor}</td>
                <td className="p-3 text-sm">#{p.cuota}</td>
                <td className="p-3 pr-5 text-right text-sm font-medium">{fmtL(p.monto)}</td>
                <td className="p-3 pr-5 text-right">
                  <button
                    onClick={() => onOpen(p.loan_id)}
                    data-testid={`view-loan-${p.codigo}`}
                    className="text-agro-yellow text-xs hover:underline"
                  >
                    Ver <CaretRightIcon size={10} weight="bold" className="inline" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ListView loans={loans} onOpen={onOpen} />
    </div>
  );
}

function ListView({ loans, onOpen }) {
  if (!loans.length) {
    return (
      <div className="bg-card border border-border/60 rounded-lg p-10 text-center">
        <HandCoinsIcon size={32} className="text-text-muted mx-auto mb-3" weight="duotone" />
        <div className="text-text-secondary text-sm">No hay préstamos registrados</div>
      </div>
    );
  }
  return (
    <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
      <table className="w-full">
        <thead className="bg-sidebar">
          <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
            <th className="p-3 pl-5">Código</th>
            <th className="p-3">Acreedor</th>
            <th className="p-3">Tipo</th>
            <th className="p-3 text-right">Monto original</th>
            <th className="p-3 text-right">Saldo capital</th>
            <th className="p-3">Tasa</th>
            <th className="p-3">Estado</th>
            <th className="p-3 pr-5"></th>
          </tr>
        </thead>
        <tbody>
          {loans.map((l) => (
            <tr key={l.id} className="border-t border-border/30 hover:bg-white/[0.02]" data-testid={`loan-row-${l.codigo}`}>
              <td className="p-3 pl-5 font-mono text-xs text-agro-green">{l.codigo}</td>
              <td className="p-3 text-sm">{l.acreedor_nombre}</td>
              <td className="p-3 text-xs text-text-secondary uppercase">{l.acreedor_tipo}</td>
              <td className="p-3 text-right text-sm">{fmtL(l.monto_original)}</td>
              <td className="p-3 text-right text-sm font-medium">{fmtL(l.saldo_capital)}</td>
              <td className="p-3 text-xs text-text-secondary">{l.tasa_anual}% anual</td>
              <td className="p-3"><EstadoBadge e={l.estado} /></td>
              <td className="p-3 pr-5 text-right">
                <button
                  onClick={() => onOpen(l.id)}
                  data-testid={`open-loan-${l.codigo}`}
                  className="text-agro-yellow text-xs hover:underline"
                >
                  Detalle <CaretRightIcon size={10} weight="bold" className="inline" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EstadoBadge({ e }) {
  const map = {
    borrador: "bg-app text-text-secondary border-border",
    vigente: "bg-agro-green/10 text-agro-green border-agro-green/30",
    cancelado: "bg-blue-500/10 text-blue-400 border-blue-500/30",
    anulado: "bg-red-500/10 text-red-400 border-red-500/30",
  };
  return <span className={`text-[10px] uppercase px-2 py-0.5 rounded border ${map[e] || ""}`}>{e}</span>;
}

function LoanForm({ banks, onClose, onSaved }) {
  const [form, setForm] = useState({
    acreedor_tipo: "banco", acreedor_nombre: "", concepto: "",
    monto_original: 0, tasa_anual: 12, plazo_meses: 12, frecuencia: "mensual",
    fecha_otorgamiento: new Date().toISOString().slice(0, 10),
    fecha_primer_pago: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
    moneda: "HNL", tasa_cambio: 1, amortizacion: "frances",
    cuenta_banco_destino_id: "", cuenta_pasivo_codigo: "2201",
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.acreedor_nombre.trim()) return toast.error("Acreedor requerido");
    if (form.monto_original <= 0 || form.plazo_meses <= 0) return toast.error("Monto y plazo deben ser > 0");
    try {
      const payload = {
        ...form,
        monto_original: Number(form.monto_original),
        tasa_anual: Number(form.tasa_anual),
        plazo_meses: Number(form.plazo_meses),
        tasa_cambio: Number(form.tasa_cambio || 1),
        fecha_otorgamiento: new Date(form.fecha_otorgamiento).toISOString(),
        fecha_primer_pago: new Date(form.fecha_primer_pago).toISOString(),
      };
      await api.post("/accounting/loans", payload);
      toast.success("Préstamo creado. Ahora puedes desembolsarlo.");
      onSaved();
    } catch (er) { toast.error(apiErr(er)); }
  };

  const m = useModalForm({ value: form, onClose: onClose, onSave: submit });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="loan-modal">
        <div className="p-5 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-agro-yellow">PRD 4.8 · Préstamo</div>
            <h3 className="font-heading text-lg font-semibold mt-0.5">Registrar préstamo</h3>
          </div>
          <button aria-label="Cerrar" onClick={m.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
        </div>
        <form onSubmit={m.guardar} className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Tipo de acreedor *</label>
              <select
                value={form.acreedor_tipo}
                onChange={(e) => setForm({ ...form, acreedor_tipo: e.target.value })}
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              >
                <option value="banco">Banco</option>
                <option value="proveedor">Proveedor</option>
                <option value="persona">Persona</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Acreedor *</label>
              <input
                data-testid="loan-acreedor"
                value={form.acreedor_nombre}
                onChange={(e) => setForm({ ...form, acreedor_nombre: e.target.value })}
                placeholder="ej. BAC Honduras"
                required
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Concepto / Descripción</label>
            <input
              value={form.concepto}
              onChange={(e) => setForm({ ...form, concepto: e.target.value })}
              placeholder="ej. Capital de trabajo zafra 2026"
              className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
            />
          </div>

          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Monto *</label>
              <input
                data-testid="loan-monto"
                type="number" step="0.01" min="0"
                value={form.monto_original}
                onChange={(e) => setForm({ ...form, monto_original: e.target.value })}
                required
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Moneda</label>
              <select
                value={form.moneda}
                onChange={(e) => setForm({ ...form, moneda: e.target.value })}
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              >
                <option value="HNL">HNL</option>
                <option value="USD">USD</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Tasa anual % *</label>
              <input
                data-testid="loan-tasa"
                type="number" step="0.01" min="0"
                value={form.tasa_anual}
                onChange={(e) => setForm({ ...form, tasa_anual: e.target.value })}
                required
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Plazo (cuotas) *</label>
              <input
                data-testid="loan-plazo"
                type="number" step="1" min="1"
                value={form.plazo_meses}
                onChange={(e) => setForm({ ...form, plazo_meses: e.target.value })}
                required
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              />
            </div>
          </div>

          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Frecuencia</label>
              <select
                value={form.frecuencia}
                onChange={(e) => setForm({ ...form, frecuencia: e.target.value })}
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              >
                <option value="quincenal">Quincenal</option>
                <option value="mensual">Mensual</option>
                <option value="bimestral">Bimestral</option>
                <option value="trimestral">Trimestral</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Amortización</label>
              <select
                value={form.amortizacion}
                onChange={(e) => setForm({ ...form, amortizacion: e.target.value })}
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              >
                <option value="frances">Francés (cuota fija)</option>
                <option value="aleman">Alemán (capital fijo)</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Fecha otorgamiento</label>
              <input
                type="date"
                value={form.fecha_otorgamiento}
                onChange={(e) => setForm({ ...form, fecha_otorgamiento: e.target.value })}
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Fecha 1ra cuota</label>
              <input
                type="date"
                value={form.fecha_primer_pago}
                onChange={(e) => setForm({ ...form, fecha_primer_pago: e.target.value })}
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              />
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">Cuenta bancaria destino del desembolso</label>
            <select
              data-testid="loan-banco"
              value={form.cuenta_banco_destino_id}
              onChange={(e) => setForm({ ...form, cuenta_banco_destino_id: e.target.value })}
              className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
            >
              <option value="">— Seleccionar al desembolsar —</option>
              {banks.map((b) => <option key={b.id} value={b.id}>{b.codigo} · {b.nombre}</option>)}
            </select>
            <p className="text-[10px] text-text-muted mt-1">Al desembolsar se hará un asiento: Debe Banco / Haber Préstamo (Pasivo 2201).</p>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={m.cerrar} className="flex-1 bg-app border border-border hover:bg-white/[0.03] text-text-primary rounded-md py-2.5 text-sm">Cancelar</button>
            <button disabled={m.saving}
              type="submit"
              data-testid="save-loan-btn"
              className="flex-1 bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md py-2.5 text-sm"
            >
              Registrar préstamo
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function LoanDetail({ loanId, banks, onClose, onChange }) {
  const [loan, setLoan] = useState(null);
  const [payCuota, setPayCuota] = useState(null);
  const [fechaDes, setFechaDes] = useState(new Date().toISOString().slice(0, 10));

  const load = async () => {
    const { data } = await api.get(`/accounting/loans/${loanId}`);
    setLoan(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [loanId]);

  const disburse = async () => {
    if (!loan.cuenta_banco_destino_id) {
      return toast.error("Asigna una cuenta bancaria destino antes de desembolsar");
    }
    if (!window.confirm(`Desembolsar ${loan.codigo} a la cuenta bancaria seleccionada?`)) return;
    try {
      await api.post(`/accounting/loans/${loanId}/disburse`, { cuenta_banco_id: loan.cuenta_banco_destino_id, fecha_desembolso: fechaDes });
      toast.success("Préstamo desembolsado. Asiento contable creado.");
      await load(); onChange();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const m = useModalForm({ onClose: onClose });
  if (!loan) return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
      <div className="bg-card border border-border rounded-lg p-8 text-text-muted">Cargando…</div>
    </div>
  );

  const cuotasPag = loan.cuotas.filter((c) => c.estado === "pagada").length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-5xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="loan-detail-modal">
        <div className="p-5 border-b border-border flex items-start justify-between sticky top-0 bg-card z-10">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-agro-yellow">Préstamo · {loan.codigo}</div>
            <h3 className="font-heading text-lg font-semibold mt-0.5">{loan.acreedor_nombre}</h3>
            <div className="text-xs text-text-muted mt-1">
              {loan.concepto || "Sin concepto"} · {loan.tasa_anual}% · {loan.plazo_meses} cuotas {loan.frecuencia}es · {loan.amortizacion}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <EstadoBadge e={loan.estado} />
            <button aria-label="Cerrar" onClick={m.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
          </div>
        </div>

        <div className="p-5 space-y-5">
          <div className="grid grid-cols-4 gap-3">
            <KpiSmall label="Monto original" value={fmtL(loan.monto_original)} />
            <KpiSmall label="Saldo capital" value={fmtL(loan.saldo_capital)} highlight />
            <KpiSmall label="Cuotas pagadas" value={`${cuotasPag} / ${loan.cuotas.length}`} />
            <KpiSmall label="Moneda" value={loan.moneda} />
          </div>

          {loan.estado === "borrador" && (
            <div className="bg-agro-yellow/10 border border-agro-yellow/30 rounded-md p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <WarningCircleIcon size={20} className="text-agro-yellow" weight="duotone" />
                <div>
                  <div className="text-sm text-agro-yellow font-medium">Préstamo en borrador</div>
                  <div className="text-xs text-text-secondary mt-0.5">
                    Pendiente de desembolso. Al desembolsar se acreditará la cuenta bancaria seleccionada y se generará el asiento del pasivo.
                  </div>
                </div>
              </div>
              {!loan.cuenta_banco_destino_id ? (
                <select
                  value={loan.cuenta_banco_destino_id || ""}
                  onChange={async (e) => {
                    const id = e.target.value;
                    if (!id) return;
                    // PUT no implementado: se permite la actualización solo en backend al desembolso; aquí permitimos manualmente
                    // Para simplificar: avisamos al usuario
                    setLoan({ ...loan, cuenta_banco_destino_id: id });
                    toast.message("Cuenta seleccionada en memoria. Ahora pulsa Desembolsar.");
                  }}
                  className="bg-app border border-border rounded-md px-3 py-2 text-xs"
                  data-testid="select-bank-disburse"
                >
                  <option value="">— Seleccionar banco —</option>
                  {banks.map((b) => <option key={b.id} value={b.id}>{b.codigo} · {b.nombre}</option>)}
                </select>
              ) : null}
              <input type="date" value={fechaDes} onChange={(e) => setFechaDes(e.target.value)} title="Fecha de desembolso"
                data-testid="disburse-fecha" className="bg-app border border-border rounded-md px-2 py-2 text-xs" />
              <button
                onClick={disburse}
                data-testid="disburse-loan-btn"
                disabled={!loan.cuenta_banco_destino_id}
                className="bg-agro-green hover:bg-agro-green-hover disabled:opacity-50 text-black font-semibold rounded-md px-4 py-2 text-sm flex items-center gap-2"
              >
                <PaperPlaneTiltIcon size={14} weight="bold" /> Desembolsar
              </button>
            </div>
          )}

          {/* Tabla de amortización */}
          <div className="bg-card border border-border/60 rounded-md overflow-hidden">
            <div className="p-3 border-b border-border/60 flex items-center justify-between bg-sidebar/40">
              <h4 className="font-heading text-xs uppercase tracking-wider text-text-muted">Tabla de amortización</h4>
              <span className="text-[10px] text-text-muted">{loan.cuotas.length} cuotas</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-sidebar/30">
                  <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                    <th className="p-2.5 pl-4">#</th>
                    <th className="p-2.5">Fecha</th>
                    <th className="p-2.5 text-right">Capital</th>
                    <th className="p-2.5 text-right">Interés</th>
                    <th className="p-2.5 text-right">Cuota</th>
                    <th className="p-2.5 text-right">Saldo</th>
                    <th className="p-2.5">Estado</th>
                    <th className="p-2.5 pr-4 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {loan.cuotas.map((c) => (
                    <tr key={c.numero} className="border-t border-border/30 hover:bg-white/[0.02]" data-testid={`cuota-${c.numero}`}>
                      <td className="p-2.5 pl-4 font-mono text-xs">#{c.numero}</td>
                      <td className="p-2.5 text-xs">{fmtDate(c.fecha)}</td>
                      <td className="p-2.5 text-right text-xs">{fmtL(c.capital)}</td>
                      <td className="p-2.5 text-right text-xs text-text-secondary">{fmtL(c.interes)}</td>
                      <td className="p-2.5 text-right text-sm font-medium">{fmtL(c.cuota)}</td>
                      <td className="p-2.5 text-right text-xs text-text-muted">{fmtL(c.saldo)}</td>
                      <td className="p-2.5">
                        {c.estado === "pagada"
                          ? <span className="text-[10px] uppercase px-2 py-0.5 rounded bg-agro-green/10 text-agro-green border border-agro-green/30 inline-flex items-center gap-1"><CheckCircleIcon size={10} weight="fill" /> Pagada</span>
                          : <span className="text-[10px] uppercase px-2 py-0.5 rounded bg-app text-text-secondary border border-border">Pendiente</span>}
                      </td>
                      <td className="p-2.5 pr-4 text-right">
                        {loan.estado === "vigente" && c.estado === "pendiente" && (
                          <button
                            onClick={() => setPayCuota(c)}
                            data-testid={`pay-cuota-${c.numero}`}
                            className="text-agro-green hover:underline text-xs"
                          >
                            Pagar
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {payCuota && (
        <PayCuotaModal
          loan={loan}
          cuota={payCuota}
          banks={banks}
          onClose={() => setPayCuota(null)}
          onPaid={async () => {
            setPayCuota(null);
            await load();
            onChange();
          }}
        />
      )}
    </div>
  );
}

function KpiSmall({ label, value, highlight }) {
  return (
    <div className="bg-app border border-border/60 rounded-md p-3">
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className={`font-heading text-base font-semibold mt-1 ${highlight ? "text-agro-green" : "text-text-primary"}`}>{value}</div>
    </div>
  );
}

function PayCuotaModal({ loan, cuota, banks, onClose, onPaid }) {
  const [bankId, setBankId] = useState(loan.cuenta_banco_destino_id || "");
  const [capital, setCapital] = useState(cuota.capital);
  const [interes, setInteres] = useState(cuota.interes);
  const [referencia, setReferencia] = useState("");
  const total = Number(capital || 0) + Number(interes || 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!bankId) return toast.error("Selecciona la cuenta bancaria de pago");
    try {
      await api.post(`/accounting/loans/${loan.id}/pay`, {
        cuota_numero: cuota.numero,
        cuenta_banco_id: bankId,
        monto_capital: Number(capital),
        monto_interes: Number(interes),
        referencia,
      });
      toast.success(`Cuota #${cuota.numero} pagada. Asiento generado.`);
      onPaid();
    } catch (er) { toast.error(apiErr(er)); }
  };

  const m = useModalForm({ value: { bankId, capital, interes, referencia }, onClose: onClose, onSave: submit, mensaje: "¿Está seguro de que desea registrar el pago de la cuota?" });
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" {...m.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="pay-cuota-modal">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-agro-yellow">Pago de cuota</div>
            <h3 className="font-heading text-base font-semibold mt-0.5">Cuota #{cuota.numero} · {loan.codigo}</h3>
          </div>
          <button aria-label="Cerrar" onClick={m.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={18} /></button>
        </div>
        <form onSubmit={m.guardar} className="p-4 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1 block">Cuenta bancaria de pago *</label>
            <select
              value={bankId}
              onChange={(e) => setBankId(e.target.value)}
              required
              className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
              data-testid="pay-cuenta-banco"
            >
              <option value="">— Seleccionar —</option>
              {banks.map((b) => <option key={b.id} value={b.id}>{b.codigo} · {b.nombre} · {fmtL(b.saldo_actual || 0)}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1 block">Capital</label>
              <input type="number" step="0.01" value={capital} onChange={(e) => setCapital(e.target.value)} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1 block">Interés</label>
              <input type="number" step="0.01" value={interes} onChange={(e) => setInteres(e.target.value)} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1 block">Referencia (cheque, transferencia)</label>
            <input value={referencia} onChange={(e) => setReferencia(e.target.value)} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" placeholder="Opcional" />
          </div>
          <div className="bg-agro-green/10 border border-agro-green/30 rounded-md p-3 text-sm flex items-center justify-between">
            <span className="text-agro-green font-medium">Total a pagar</span>
            <span className="font-heading text-agro-green text-lg">{fmtL(total)}</span>
          </div>
          <button disabled={m.saving} type="submit" data-testid="confirm-pay-cuota" className="w-full bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md py-2.5 text-sm">
            Confirmar pago
          </button>
        </form>
      </div>
    </div>
  );
}
